import React from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Alert, Grid, CircularProgress, Chip, Box, Typography, Divider } from '@mui/material';

import { useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import ArticleIcon from '@mui/icons-material/Article';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';

import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputCountryDropdown, FormInputDropdown } from 'components/form/FormInputDropdown';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputPaginatedSpaceSelect } from 'components/security/PaginatedSpaceSelect';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';

import {
  SPACE_VIEW_QUERY,
  SEMPLANS_LIST_QUERY,
  UPDATE_SPACE_MUTATION,
  IMPORT_DEFAULTOFFERTEMPLATES_MUTATION,
  IMPORT_DEFAULTEMAILTEMPLATES_MUTATION,
  IMPORT_DEFAULTPRODUCTS_MUTATION,
  IMPORT_DEFAULTPRICELISTS_MUTATION,
  REFETCH_SPACES_QUERIES,
  EVICT_SPACES_QUERIES,
  HOTEL_LIST_QUERY,
} from '../gql';

import {
  REFETCH_OFFERTEMPLATES_QUERIES,
  EVICT_OFFERTEMPLATES_QUERIES,
  REFETCH_EMAILTEMPLATES_QUERIES,
  EVICT_EMAILTEMPLATES_QUERIES,
} from '../../content/gql';

import { Space, SEMPlan, ELicenseSEMPlan, ECountryCode } from '__generated__/graphql';
import { userSelector, canEditAdminSpaceId } from 'helper/security';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';

interface SpaceProps {
  id: number;
}
interface SpaceFormProps {
  data: Space;
  plans: SEMPlan[];
}

const validationSchema = yup.object().shape({
  name: yup.string().required().label(i18next.t('space-name')),
  country: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label(i18next.t('space-country')),
  parentId: yup.number().nullable().label(i18next.t('space-parentspace')),
  plan: yup.string().nullable(),
  hasEpilo: yup.boolean(),
});

function SpaceForm(props: SpaceFormProps) {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const navigate = useNavigate();

  const user = userSelector()!;
  const canEdit = user.isSeminargo;

  const [updateMutateFunction] = useMutation(UPDATE_SPACE_MUTATION);
  const [importOfferTemplatesMutateFunction, { loading: importOfferTemplatesLoading }] = useMutation(IMPORT_DEFAULTOFFERTEMPLATES_MUTATION);
  const [importEmailTemplatesMutateFunction, { loading: importEmailTemplatesLoading }] = useMutation(IMPORT_DEFAULTEMAILTEMPLATES_MUTATION);
  const [importProductsMutateFunction, { loading: importProductsLoading }] = useMutation(IMPORT_DEFAULTPRODUCTS_MUTATION);
  const [importPricelistsMutateFunction, { loading: importPricelistsLoading }] = useMutation(IMPORT_DEFAULTPRICELISTS_MUTATION);

  const hotelsQuery = useQuery(HOTEL_LIST_QUERY, {
    variables: { spaceId: props.data.id, skip: 0, take: 100 },
  });

  type SpaceFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: Space): SpaceFormType => ({
    ...obj,
    parentId: obj.parent ? obj.parent.id : 0,
    plan: obj.activeSEMPlan ? obj.activeSEMPlan.semPlan.plan : null,
    hasEpilo: obj.hasEpilo || false,
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client },
    defaultValues: toFormSchema((props.data || {}) as Space),
  });

  const onSubmit = async (values: SpaceFormType) => {
    try {
      const res = await updateMutateFunction({
        variables: {
          id: props.data.id,
          data: {
            name: values.name,
            country: values.country,
            parentId: values.parentId || null,
            ...(user.isSeminargo ? { semPlan: values.plan as ELicenseSEMPlan | null } : {}),
            hasEpilo: values.hasEpilo,
          },
        },
        update: cache => EVICT_SPACES_QUERIES(cache),
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_SPACES_QUERIES(props.data.id),
      });
      const updated = toFormSchema((res.data!.updateSpace || {}) as Space);
      (updated as any).plan = (values.plan as any) ?? null;
      reset(updated);
      dispatchMessage(dispatch, i18next.t('space-updated'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t('spaces-list-page-title'), props.data])}</title>
      </Helmet>
      <Grid container spacing={3}>
        <UnsavedChangesPrompt isDirty={isDirty} />
        <Grid item xs={12} sm={4}>
          <FormInputText name="name" control={control} label={i18next.t('space-name')} required disabled={!canEdit} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputPaginatedSpaceSelect
            name="parentId"
            control={control}
            disabled={!canEdit}
            label={i18next.t('space-parentspace') as string}
            excludeSpaceIds={[props.data.id]}
            allowEmpty
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputCountryDropdown name="country" control={control} label={i18next.t('space-country')} disabled={!canEdit} required />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            seminargo
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormInputCheckbox name="hasEpilo" control={control} label={String(i18next.t('space-has-epilo'))} disabled={!canEdit} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            SEM
          </Typography>
          <Box sx={{ maxWidth: 300 }}>
            <FormInputDropdown
              name="plan"
              control={control}
              label={i18next.t('space-license')}
              options={[
                {
                  label: <em>{i18next.t('generic-selection-empty')}</em>,
                  value: '',
                },
                ...props.plans.map(p => ({
                  value: p.plan,
                  label: p.label,
                })),
              ]}
              disabled={!canEdit}
            />
          </Box>
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <ConfirmationButton
                variant="contained"
                color="secondary"
                startIcon={importOfferTemplatesLoading ? <CircularProgress size={24} /> : <ArticleIcon />}
                disabled={importOfferTemplatesLoading || !props.data.activeSEMPlan}
                confirmationQuestion={i18next.t('space-confirm-importoffertemplates')}
                confirmationTitle={i18next.t('space-confirm-importoffertemplates-title')}
                onConfirm={async () => {
                  try {
                    const res = await importOfferTemplatesMutateFunction({
                      variables: {
                        targetSpaceId: props.data.id,
                      },
                      update: cache => EVICT_OFFERTEMPLATES_QUERIES(cache),
                      awaitRefetchQueries: true,
                      refetchQueries: REFETCH_OFFERTEMPLATES_QUERIES(),
                    });
                    dispatchMessage(dispatch, i18next.t('space-offertemplatesimported'));
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('space-importoffertemplates')}
              </ConfirmationButton>
              <ConfirmationButton
                variant="contained"
                color="secondary"
                startIcon={importEmailTemplatesLoading ? <CircularProgress size={24} /> : <ArticleIcon />}
                disabled={importEmailTemplatesLoading || !props.data.activeSEMPlan}
                confirmationQuestion={i18next.t('space-confirm-importemailtemplates')}
                confirmationTitle={i18next.t('space-confirm-importemailtemplates-title')}
                onConfirm={async () => {
                  try {
                    const res = await importEmailTemplatesMutateFunction({
                      variables: {
                        targetSpaceId: props.data.id,
                      },
                      update: cache => EVICT_EMAILTEMPLATES_QUERIES(cache),
                      awaitRefetchQueries: true,
                      refetchQueries: REFETCH_EMAILTEMPLATES_QUERIES(),
                    });
                    dispatchMessage(dispatch, i18next.t('space-emailtemplatesimported'));
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('space-importemailtemplates')}
              </ConfirmationButton>
              <ConfirmationButton
                variant="contained"
                color="secondary"
                startIcon={importProductsLoading ? <CircularProgress size={24} /> : <InventoryIcon />}
                disabled={importProductsLoading || !props.data.activeSEMPlan}
                confirmationQuestion={i18next.t('space-confirm-importproducts')}
                confirmationTitle={i18next.t('space-confirm-importproducts-title')}
                onConfirm={async () => {
                  try {
                    await importProductsMutateFunction({
                      variables: {
                        targetSpaceId: props.data.id,
                        sem: true,
                        epilo: false,
                      },
                    });
                    dispatchMessage(dispatch, i18next.t('space-productsimported'));
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('space-importproducts')}
              </ConfirmationButton>
              <ConfirmationButton
                variant="contained"
                color="secondary"
                startIcon={importPricelistsLoading ? <CircularProgress size={24} /> : <ReceiptIcon />}
                disabled={importPricelistsLoading || !props.data.activeSEMPlan}
                confirmationQuestion={i18next.t('space-confirm-importpricelists')}
                confirmationTitle={i18next.t('space-confirm-importpricelists-title')}
                onConfirm={async () => {
                  try {
                    await importPricelistsMutateFunction({
                      variables: {
                        targetSpaceId: props.data.id,
                        sem: true,
                        epilo: false,
                      },
                    });
                    dispatchMessage(dispatch, i18next.t('space-pricelistscreated'));
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('space-importpricelists')}
              </ConfirmationButton>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {i18next.t('space-users')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {props.data.users?.map(u => (
              <Chip key={u.id} icon={<PersonIcon />} label={u.name} onClick={() => navigate(`/settings/users/${u.id}`)} />
            ))}
            {props.data.members
              ?.filter(m => !props.data.users?.some(u => u.id === m.user.id))
              .map(m => (
                <Chip
                  key={m.user.id}
                  icon={m.isAdmin ? <SupervisorAccountIcon /> : <PersonIcon />}
                  label={m.user.name}
                  onClick={() => navigate(`/settings/users/${m.user.id}`)}
                />
              ))}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {i18next.t('sidebar-hotels')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {hotelsQuery.data?.listHotels.hotels.map(h => (
              <Chip key={h.id} icon={<BusinessIcon />} label={h.name} onClick={() => navigate(`/settings/hotels/${h.id}`)} />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12}>
          {canEdit && (
            <ConfirmationButton
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
              disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
              confirmationQuestion={i18next.t('space-confirm-update')}
              confirmationTitle={i18next.t('space-confirm-update-title')}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t('space-update')}
            </ConfirmationButton>
          )}
        </Grid>
      </Grid>
    </>
  );
}

/**
 * Space detail page wrapper that loads space and plan data.
 * Renders space form for editing space configuration and plan assignments.
 */
export default function SpaceUpdate(props: SpaceProps) {
  const userQuery = useQuery(SPACE_VIEW_QUERY, {
    variables: { id: props.id },
  });
  const plansQuery = useQuery(SEMPLANS_LIST_QUERY);

  const loading = userQuery.loading || plansQuery.loading;
  const error = userQuery.error || plansQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else return <SpaceForm data={userQuery.data!.viewSpace as Space} plans={plansQuery.data!.listSEMPlans as SEMPlan[]} />;
}
